import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import { fg } from '@atlaskit/platform-feature-flags';

import { MessageBody } from '../../../../../../..//common/ui/message-body';
import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import { WorkspaceNameMetadata } from '../../../../../../../common/ui/workspace-name-metadata';

import type { searchGraphConversation_AtlassianSearchPage$key } from './__generated__/searchGraphConversation_AtlassianSearchPage.graphql';
import { i18n } from './messages';
import { Tabs } from './tabs';

export type SearchGraphConversationProps =
	SearchGraphResultProps<searchGraphConversation_AtlassianSearchPage$key>;

export const SearchGraphConversation = ({
	node,
	position,
	cloudId,
	primaryProduct,
	selectedProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	thirdPartyConfigs,
	mainContentWidth,
}: SearchGraphConversationProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const data = useFragment(
		graphql`
			fragment searchGraphConversation_AtlassianSearchPage on SearchResultGraphDocument {
				...messageBody_AtlassianSearchPage
				...tabs_AtlassianSearchPage
				id
				url
				subtype
				integrationId
				title
				workspaceName @optIn(to: "SearchPerformanceImprovementChanges")
				tabs {
					__typename
				}
				entity {
					... on ExternalConversation {
						displayName
						memberCount
						members {
							__typename
						}
					}
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const memberCount = data.entity?.memberCount ?? data.entity?.members?.length;
	const workspaceName = data.workspaceName;
	const parts: [string, React.ReactNode][] = [
		['label', <FormattedMessage {...i18n.label} />],
		[
			'workspaceName',
			workspaceName ? <WorkspaceNameMetadata workspaceName={workspaceName} /> : null,
		],
		[
			'memberCount',
			memberCount && <FormattedMessage {...i18n.memberCount} values={{ count: memberCount }} />,
		],
	];

	const getChannelName = () => {
		if (data.entity?.displayName) {
			return `#${data.entity.displayName}`;
		}
		if (data.title) {
			return data.title.startsWith('#') ? data.title : `#${data.title}`;
		}
		return `#${formatMessage(i18n.unknownChannel)}`;
	};

	let privateChannelName;
	if (data.subtype === 'private-channel') {
		privateChannelName = (
			<>
				<LockLockedIcon label={formatMessage(i18n.privateChannel)} size="small" />
				{getChannelName()}
			</>
		);
	}

	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const tabItems = data.tabs ?? [];
	const tabs = fg('rovo_fps_enable_ms_teams_channel_tab_type_filter') ? tabItems : [];

	return (
		<SearchResult
			id={data.id}
			product={product}
			productSelected
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			description={<MessageBody integrationId={data.integrationId} node={data} />}
			icon={icon}
			headerMetadataParts={parts}
			title={privateChannelName || getChannelName()}
			url={data.url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			appMode={appMode}
			footer={
				<>
					{tabs.length > 0 && <Tabs node={data} />}
					<MentionedByLinkedResults
						linkedResults={linkedResults || []}
						thirdPartyConfigs={thirdPartyConfigs}
						partial={data}
						position={position}
						onResultClicked={onResultClicked}
						url={data.url}
						onResultContextMenu={onResultContextMenu}
						isAllProductSelected={!selectedProduct}
					/>
				</>
			}
			mainContentWidth={mainContentWidth}
		/>
	);
};
